'use client';
import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { cricketScheduleData, cricketTours, formatMatchTime, getDateLabel, ScheduleDay, CricketTour, CricketMatch } from '@/lib/cricketScheduleData';

const POLL_INTERVAL = 20000; // 20 seconds

const sportTabs = [
  { label: 'Cricket', href: '/cricket/schedule', active: true },
  { label: 'Football', href: '/football/schedule', active: false },
  { label: 'Formula 1', href: '/formula1/schedule', active: false },
  { label: 'Tennis', href: '/lawn-tennis/schedule', active: false },
  { label: 'MotoGP', href: '/motogp/schedule', active: false },
  { label: 'Golf', href: '/golf/schedule', active: false },
];

function WatchLiveButton() {
  return (
    <div className="flex items-center gap-1">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="#eb2f42">
        <path d="M6.5 6.47v11.06c0 .844.943 1.357 1.67.898l8.826-5.531a1.06 1.06 0 000-1.805L8.17 5.572c-.727-.459-1.67.054-1.67.897z" fill="#eb2f42"/>
      </svg>
      <span className="text-[11px] font-semibold italic tracking-[0.2px]" style={{fontFamily:'Saira', color:'#eb2f42'}}>WATCH LIVE</span>
    </div>
  );
}

function MatchTimeDisplay({ startTime }: { startTime: string }) {
  const { time, ampm } = formatMatchTime(startTime);
  return (
    <div className="flex flex-col items-end">
      <span className="text-[12px] font-normal" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.5)'}}>Watch Live At</span>
      <div className="flex items-baseline gap-0.5">
        <span className="text-[16px] font-medium leading-5" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.85)'}}>{time}</span>
        <span className="text-[10px] font-normal" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.85)'}}>{ampm}</span>
      </div>
    </div>
  );
}

function MatchCard({ match, isLast }: { match: CricketMatch; isLast: boolean }) {
  const isLive = match.status === 'LIVE';
  const isCompleted = match.status === 'COMPLETED' || match.status === 'ABANDONED';
  const isUpcoming = match.status === 'NOT_STARTED';
  const squad1 = match.squads[0];
  const squad2 = match.squads[1];

  return (
    <div className={`flex-1 min-w-0 ${!isLast ? 'border-r' : ''}`} style={{borderColor:'rgba(255,255,255,0.06)'}}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[12px] font-normal" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.5)'}}>{match.matchDesc}</span>
          {isLive && (
            <span className="flex items-center gap-1 text-[10px] font-bold tracking-wider" style={{color:'#eb2f42'}}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#eb2f42] animate-pulse inline-block"></span>
              LIVE
            </span>
          )}
          {match.format && (
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{backgroundColor:'rgba(255,255,255,0.08)', color:'rgba(255,255,255,0.5)', fontFamily:'Roboto'}}>
              {match.format}
            </span>
          )}
        </div>
        <div className="space-y-2">
          {/* Team 1 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img
                src={squad1?.flag}
                alt={`${squad1?.name} cricket team flag`}
                width={20}
                height={20}
                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
              />
              <span
                className="text-[14px] truncate"
                style={{fontFamily:'Roboto', letterSpacing:'0.25px', color: squad1?.isWinner ? '#ffffff' : 'rgba(255,255,255,0.85)', fontWeight: squad1?.isWinner ? 500 : 400, maxWidth:'140px'}}
              >
                {squad1?.shortName}
              </span>
            </div>
            {isCompleted && squad1?.score && (
              <span className="text-[14px] font-medium flex-shrink-0" style={{fontFamily:'Roboto', color: squad1?.isWinner ? '#ffffff' : 'rgba(255,255,255,0.85)'}}>
                {squad1.score}{squad1.wickets !== undefined ? `/${squad1.wickets}` : ''}
                {squad1.overs && <span className="text-[12px] ml-1" style={{color:'rgba(255,255,255,0.5)'}}>({squad1.overs})</span>}
              </span>
            )}
            {isLive && (
              <span className="text-[12px] flex-shrink-0" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.5)'}}>Yet to Bat</span>
            )}
          </div>
          {/* Team 2 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <img
                src={squad2?.flag}
                alt={`${squad2?.name} cricket team flag`}
                width={20}
                height={20}
                className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
              />
              <span
                className="text-[14px] truncate"
                style={{fontFamily:'Roboto', letterSpacing:'0.25px', color: squad2?.isWinner ? '#ffffff' : 'rgba(255,255,255,0.85)', fontWeight: squad2?.isWinner ? 500 : 400, maxWidth:'140px'}}
              >
                {squad2?.shortName}
              </span>
            </div>
            {isCompleted && squad2?.score && (
              <span className="text-[14px] font-medium flex-shrink-0" style={{fontFamily:'Roboto', color: squad2?.isWinner ? '#ffffff' : 'rgba(255,255,255,0.85)'}}>
                {squad2.score}{squad2.wickets !== undefined ? `/${squad2.wickets}` : ''}
                {squad2.overs && <span className="text-[12px] ml-1" style={{color:'rgba(255,255,255,0.5)'}}>({squad2.overs})</span>}
              </span>
            )}
            {isLive && (
              <span className="text-[12px] flex-shrink-0" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.5)'}}>Yet to Bat</span>
            )}
          </div>
        </div>
        {/* Bottom CTA */}
        <div className="mt-3 pt-2 flex items-center justify-between" style={{borderTop:'1px solid rgba(255,255,255,0.06)'}}>
          <div className="flex-1">
            {isLive && <WatchLiveButton />}
            {isUpcoming && <MatchTimeDisplay startTime={match.startTime} />}
            {isCompleted && match.scoreDescription && (
              <span className="text-[11px]" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.5)'}}>{match.scoreDescription}</span>
            )}
            {isCompleted && !match.scoreDescription && (
              <span className="text-[11px]" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.5)'}}>
                {match.status === 'ABANDONED' ? 'Match Abandoned' : 'Completed'}
              </span>
            )}
          </div>
          {match.venue && (
            <span className="text-[10px] truncate ml-2 max-w-[100px]" style={{color:'rgba(255,255,255,0.3)', fontFamily:'Roboto'}}>{match.venue}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function TourSection({ tourName, tourSlug, matches }: { tourName: string; tourSlug: string; matches: CricketMatch[] }) {
  const rows: CricketMatch[][] = [];
  for (let i = 0; i < matches.length; i += 2) {
    rows.push(matches.slice(i, i + 2));
  }

  return (
    <div>
      <Link href={`/cricket/tour/${tourSlug}/matches`} className="flex items-center gap-2 px-4 py-3 hover:bg-[rgba(255,255,255,0.03)] transition-colors">
        <h2 className="text-[16px] font-semibold truncate flex-1" style={{fontFamily:'Saira', lineHeight:'24px', color:'rgba(255,255,255,0.85)'}}>
          {tourName}
        </h2>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#ffffffd9">
          <path d="M10 6.71a.996.996 0 000 1.41L13.88 12 10 15.88a.996.996 0 101.41 1.41L16 12.7a.996.996 0 000-1.41L11.41 6.7c-.38-.38-1.02-.38-1.41.01z" fill="#ffffffd9"/>
        </svg>
      </Link>
      {rows.map((row, rowIdx) => (
        <div key={rowIdx} className="flex" style={{borderTop:'1px solid rgba(255,255,255,0.06)'}}>
          {row.map((match, idx) => (
            <Link
              key={match.id}
              href={`/cricket/tour/${match.tourSlug}/matches/${match.matchSlug}/live-match-info`}
              className="flex-1 hover:bg-[rgba(255,255,255,0.03)] transition-colors"
            >
              <MatchCard match={match} isLast={idx === row.length - 1 || row.length === 1} />
            </Link>
          ))}
          {row.length === 1 && <div className="flex-1" />}
        </div>
      ))}
    </div>
  );
}

interface CricketSchedulePageProps {
  initialData?: ScheduleDay[];
  initialTours?: CricketTour[];
}

export default function CricketSchedulePage({ initialData, initialTours }: CricketSchedulePageProps) {
  const [scheduleData, setScheduleData] = useState<ScheduleDay[]>(initialData || cricketScheduleData);
  const [tours, setTours] = useState<CricketTour[]>(initialTours || cricketTours);
  const [selectedTours, setSelectedTours] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLiveFilter, setIsLiveFilter] = useState(false);
  const [isStreamingFilter, setIsStreamingFilter] = useState(false);
  const [activeDateIdx, setActiveDateIdx] = useState(1);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchSchedule = useCallback(async () => {
    try {
      const res = await fetch('/api/cricket-schedule', { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (data?.schedule?.length) {
          setScheduleData(data.schedule);
          if (data.tours?.length) setTours(data.tours);
        }
      }
    } catch {
      // silently keep existing data on error
    }
  }, []);

  useEffect(() => {
    intervalRef.current = setInterval(fetchSchedule, POLL_INTERVAL);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchSchedule]);

  const liveMatchCount = scheduleData.reduce((acc, day) => {
    return acc + day.tours.reduce((tacc, t) => {
      return tacc + t.matches.filter(m => m.status === 'LIVE').length;
    }, 0);
  }, 0);

  const filteredTours = tours.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeDay = scheduleData[activeDateIdx] || scheduleData[0];

  const filteredDayTours = (activeDay?.tours || []).filter(t => {
    if (selectedTours.length === 0) return true;
    return selectedTours.includes(t.tour.id);
  }).filter(t => {
    if (!isLiveFilter) return true;
    return t.matches.some(m => m.status === 'LIVE');
  }).filter(t => {
    if (!isStreamingFilter) return true;
    return t.matches.some(m => m.streamingStatus === 'STARTED' || m.streamingStatus === 'NOT_STARTED');
  });

  const handleTourToggle = (tourId: string) => {
    setSelectedTours(prev =>
      prev.includes(tourId) ? prev.filter(id => id !== tourId) : [...prev, tourId]
    );
  };

  const dateButtons = scheduleData.map((day, idx) => {
    const d = new Date(day.date);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return {
      date: day.date,
      label: getDateLabel(day.date),
      shortDate: `${d.getDate()} ${months[d.getMonth()]}`,
      idx
    };
  });

  const activeBtn = dateButtons[activeDateIdx];
  const isSpecialLabel = activeBtn?.label === 'Today' || activeBtn?.label === 'Tomorrow' || activeBtn?.label === 'Yesterday';

  return (
    <div className="min-h-screen" style={{backgroundColor:'#07090d'}}>
      {/* Sport Tabs Header */}
      <div className="sticky top-14 z-40" style={{backgroundColor:'#07090d', borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
        <div className="flex items-center px-6 py-4 gap-4">
          <h1 className="text-[24px] font-semibold text-white flex-shrink-0" style={{fontFamily:'Saira', lineHeight:'32px'}}>Schedule</h1>
          <nav className="flex items-center gap-1 overflow-x-auto flex-1" style={{scrollbarWidth:'none'}}>
            {sportTabs.map((tab) => (
              <Link
                key={tab.label}
                href={tab.href}
                className="flex-shrink-0 px-4 py-1.5 rounded-full text-[14px] transition-colors"
                style={{
                  fontFamily: 'Saira',
                  backgroundColor: tab.active ? '#eb2f42' : 'transparent',
                  color: tab.active ? '#ffffff' : 'rgba(255,255,255,0.85)',
                  fontWeight: tab.active ? 600 : 400,
                }}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex" style={{minHeight:'calc(100vh - 112px)'}}>
        {/* Left Sidebar */}
        <div
          className="w-[280px] flex-shrink-0 sticky top-[112px] flex flex-col hidden lg:flex"
          style={{height:'calc(100vh - 112px)', borderRight:'1px solid rgba(255,255,255,0.06)', overflow:'hidden'}}
        >
          <div className="px-4 pt-4 pb-2 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[18px] font-semibold" style={{fontFamily:'Saira', lineHeight:'28px', color:'rgba(255,255,255,0.85)'}}>Cricket Tours</h2>
              <button
                onClick={() => { setSelectedTours([]); setSearchQuery(''); }}
                className="text-[12px] font-semibold tracking-[0.1px]"
                style={{fontFamily:'Saira', color: selectedTours.length > 0 ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.24)'}}
              >
                CLEAR
              </button>
            </div>
            <div
              className="flex items-center gap-2 px-3 rounded mb-4"
              style={{border:'1px solid rgba(255,255,255,0.06)', backgroundColor:'#07090d', height:'40px'}}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffffd9">
                <path fillRule="evenodd" clipRule="evenodd" d="M11 2a9 9 0 105.618 16.032l3.675 3.675a1 1 0 001.414-1.414l-3.675-3.675A9 9 0 0011 2zm-7 9a7 7 0 1114 0 7 7 0 01-14 0z" fill="#ffffffd9"/>
              </svg>
              <input
                type="text"
                placeholder="Search tours..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder-[rgba(255,255,255,0.3)]"
                style={{fontFamily:'Roboto'}}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto pb-8" style={{scrollbarWidth:'none'}}>
            {filteredTours.map((tour) => (
              <div
                key={tour.id}
                role="button"
                onClick={() => handleTourToggle(tour.id)}
                className="flex items-start gap-3 px-4 py-2 cursor-pointer hover:bg-[rgba(255,255,255,0.03)] transition-colors"
              >
                <div
                  className="flex-shrink-0 mt-0.5 flex items-center justify-center rounded-sm"
                  style={{
                    width:'16px', height:'16px', minWidth:'16px', minHeight:'16px',
                    border: selectedTours.includes(tour.id) ? '1px solid #eb2f42' : '1px solid rgba(255,255,255,0.24)',
                    backgroundColor: selectedTours.includes(tour.id) ? '#eb2f42' : 'transparent',
                  }}
                >
                  {selectedTours.includes(tour.id) && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span
                  className="text-[12px] leading-4"
                  style={{fontFamily:'Roboto', fontWeight:400, letterSpacing:'0.2px', color:'rgba(255,255,255,0.85)', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}
                >
                  {tour.name}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 min-w-0">
          {/* Sticky Date + Filter Bar */}
          <div
            className="sticky top-[112px] z-30 flex items-center justify-between px-4 py-3"
            style={{backgroundColor:'#07090d', borderBottom:'1px solid rgba(255,255,255,0.06)'}}
          >
            {/* Date Selector */}
            <div className="relative">
              <button onClick={() => setShowDatePicker(!showDatePicker)} className="flex items-center gap-2">
                {isSpecialLabel && (
                  <span className="text-[18px] font-semibold" style={{fontFamily:'Saira', lineHeight:'28px', color:'rgba(255,255,255,0.85)'}}>
                    {activeBtn?.label}
                  </span>
                )}
                <span className="text-[18px] font-normal" style={{fontFamily:'Saira', lineHeight:'28px', letterSpacing:'0.1px', color:'rgba(255,255,255,0.85)'}}>
                  {activeBtn?.shortDate}
                </span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffffd9">
                  <path d="M6.704 9.293a.996.996 0 011.41 0l3.88 3.88 3.88-3.88a.996.996 0 111.41 1.41l-4.59 4.59a.996.996 0 01-1.41 0l-4.59-4.59c-.38-.38-.38-1.02.01-1.41z" fill="#ffffffd9"/>
                </svg>
              </button>
              {showDatePicker && (
                <div
                  className="absolute top-full left-0 mt-1 rounded-lg shadow-xl z-50 py-1"
                  style={{backgroundColor:'#202532', border:'1px solid rgba(255,255,255,0.1)', minWidth:'180px'}}
                >
                  {dateButtons.map((btn) => (
                    <button
                      key={btn.idx}
                      onClick={() => { setActiveDateIdx(btn.idx); setShowDatePicker(false); }}
                      className="w-full text-left px-4 py-2 text-[14px] hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                      style={{fontFamily:'Saira', color: activeDateIdx === btn.idx ? '#ffffff' : 'rgba(255,255,255,0.85)', fontWeight: activeDateIdx === btn.idx ? 600 : 400}}
                    >
                      {btn.label !== btn.shortDate ? `${btn.label} · ${btn.shortDate}` : btn.shortDate}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsLiveFilter(!isLiveFilter)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] transition-colors"
                style={{
                  fontFamily:'Saira',
                  backgroundColor: isLiveFilter ? 'rgba(235,47,66,0.15)' : 'rgba(255,255,255,0.08)',
                  border: isLiveFilter ? '1px solid #eb2f42' : '1px solid rgba(255,255,255,0.12)',
                  color:'white', letterSpacing:'0.1px'
                }}
              >
                {liveMatchCount > 0 && <span className="w-1.5 h-1.5 rounded-full bg-[#eb2f42] animate-pulse"></span>}
                Live Now{liveMatchCount > 0 ? ` (${liveMatchCount})` : ''}
              </button>
              <button
                onClick={() => setIsStreamingFilter(!isStreamingFilter)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] transition-colors"
                style={{
                  fontFamily:'Saira',
                  backgroundColor: isStreamingFilter ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                  border: isStreamingFilter ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.12)',
                  color:'white', letterSpacing:'0.1px'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="#ffffffd9">
                  <path d="M6.125 11.365L11.365 8l-5.24-3.365v6.73zM8 15.917a7.711 7.711 0 01-3.087-.624A7.997 7.997 0 012.4 13.601a7.991 7.991 0 01-1.693-2.513 7.704 7.704 0 01-.624-3.087c0-1.095.208-2.124.624-3.087A7.997 7.997 0 012.398 2.4 7.993 7.993 0 014.912.707 7.704 7.704 0 017.999.083c1.094 0 2.124.208 3.087.624.963.415 1.802.98 2.514 1.692a7.992 7.992 0 011.693 2.513c.416.963.623 1.992.623 3.087a7.711 7.711 0 01-.623 3.087 7.996 7.996 0 01-1.692 2.514 7.99 7.99 0 01-2.513 1.693 7.703 7.703 0 01-3.087.624z" fill="#ffffffd9"/>
                </svg>
                Streaming
              </button>
            </div>
          </div>

          {/* Schedule List */}
          <div className="pb-20">
            {filteredDayTours.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="rgba(255,255,255,0.15)" className="mb-4">
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" fill="rgba(255,255,255,0.15)"/>
                </svg>
                <div className="text-[16px] font-medium" style={{fontFamily:'Saira', color:'rgba(255,255,255,0.3)'}}>No matches found</div>
                <div className="text-[13px] mt-1" style={{fontFamily:'Roboto', color:'rgba(255,255,255,0.2)'}}>Try adjusting your filters</div>
              </div>
            ) : (
              filteredDayTours.map((tourData) => (
                <div key={tourData.tour.id} style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                  <TourSection
                    tourName={tourData.tour.name}
                    tourSlug={tourData.tour.tourSlug}
                    matches={tourData.matches}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Scroll to Today Button */}
      <button
        onClick={() => setActiveDateIdx(scheduleData.findIndex(d => getDateLabel(d.date) === 'Today') || 0)}
        className="fixed bottom-6 right-6 flex items-center gap-2 px-4 py-2 rounded-full shadow-lg z-50 hover:bg-[#2a3040] transition-colors"
        style={{backgroundColor:'#202532', border:'1px solid rgba(255,255,255,0.12)'}}
      >
        <span className="text-[14px] font-semibold" style={{fontFamily:'Saira', color:'rgba(255,255,255,0.85)'}}>Today</span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="rgba(255,255,255,0.85)">
          <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" fill="rgba(255,255,255,0.85)"/>
        </svg>
      </button>
    </div>
  );
}
